import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { LayoutModule } from '../../../layout/layout.module';
import {
  MinioCheckStatus,
  MinioConfigurationItem,
  MinioDashboardApiService,
  MinioHealthCheck,
  MinioStatus,
} from './minio-dashboard-api.service';

interface MinioNextStep {
  title: string;
  description: string;
  state: 'ready' | 'attention' | 'planned';
}

@Component({
  selector: 'app-minio-dashboard-page',
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
  ],
  providers: [
    MinioDashboardApiService,
  ],
  templateUrl: './minio-dashboard-page.component.html',
  styleUrl: './minio-dashboard-page.component.sass',
})
export class MinioDashboardPageComponent implements OnInit {
  public status: MinioStatus | null = null;
  public loading = false;
  public errorMessage: string | null = null;

  public constructor(
    private minioApi: MinioDashboardApiService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.loadStatus();
  }

  public async loadStatus(): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const response = await this.minioApi.getStatus();
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.status = response.response;
    } catch {
      this.errorMessage = 'Не удалось получить статус MinIO.';
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  public get statusLabel(): string {
    if (!this.status) {
      return 'Проверяем';
    }

    const internalCheck = this.findCheck('internal');
    if (internalCheck?.status === 'online') {
      return this.status.configured ? 'Подключено' : 'Требует настройки';
    }

    return 'Недоступно';
  }

  public get statusTone(): 'ready' | 'attention' | 'offline' {
    if (!this.status) {
      return 'attention';
    }

    const internalCheck = this.findCheck('internal');
    if (internalCheck?.status === 'online' && this.status.configured) {
      return 'ready';
    }

    if (internalCheck?.status === 'online') {
      return 'attention';
    }

    return 'offline';
  }

  public get protectedChecksCount(): number {
    return this.status?.checks.filter((check) => check.status === 'protected').length ?? 0;
  }

  public get protectedChecksLabel(): string {
    const count = this.protectedChecksCount;
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;
    const word = lastDigit === 1 && lastTwoDigits !== 11
      ? 'проверка'
      : lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14)
        ? 'проверки'
        : 'проверок';

    return `${count} ${word} по IP`;
  }

  public get nextSteps(): MinioNextStep[] {
    const adminIpReady = this.isAdminAllowedIpReady;

    return [
      {
        title: 'S3 Browser',
        description: adminIpReady
          ? 'Доступ по ADMIN_ALLOWED_IP настроен, можно открывать MinIO Console с разрешенного IP.'
          : 'Открыть доступ к MinIO Console после замены ADMIN_ALLOWED_IP на реальный статический IP.',
        state: adminIpReady ? 'ready' : 'attention',
      },
      {
        title: 'Файлы dashboard',
        description: 'Перевести загрузку логотипов, картинок рассылок и билетов с /uploads на S3 bucket.',
        state: 'planned',
      },
      {
        title: 'Бэкапы',
        description: 'Хранить резервные копии bucket и PostgreSQL по расписанию.',
        state: 'planned',
      },
    ];
  }

  public get checkedAtLabel(): string {
    if (!this.status) {
      return '--';
    }

    return new Intl.DateTimeFormat('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(this.status.checkedAt));
  }

  public get viewerAccessTone(): 'ready' | 'attention' | 'missing' {
    const viewerAllowed = this.status?.access.viewerAllowed;
    if (viewerAllowed === true) {
      return 'ready';
    }

    if (viewerAllowed === false) {
      return 'attention';
    }

    return 'missing';
  }

  public get viewerAccessTitle(): string {
    const viewerAllowed = this.status?.access.viewerAllowed;
    if (viewerAllowed === true) {
      return 'Ваш IP разрешен';
    }

    if (viewerAllowed === false) {
      return 'IP не совпадает';
    }

    return 'IP не определен';
  }

  public get viewerAccessDescription(): string {
    const access = this.status?.access;
    if (!access) {
      return 'Данные доступа еще не загружены.';
    }

    const viewerIp = access.viewerIp ?? '--';
    const allowedIp = access.allowedIp ?? '--';
    if (access.viewerAllowed === true) {
      return `Dashboard видит ваш запрос как ${viewerIp}; MinIO Console и S3 доступны с этого компьютера.`;
    }

    if (access.viewerAllowed === false) {
      return `Dashboard видит ваш запрос как ${viewerIp}, а в allowlist указан ${allowedIp}.`;
    }

    return `Allowlist: ${allowedIp}. Текущий IP запроса определить не удалось.`;
  }

  public getCheckLabel(status: MinioCheckStatus): string {
    const labels: Record<MinioCheckStatus, string> = {
      online: 'Онлайн',
      protected: 'По IP',
      offline: 'Офлайн',
      unknown: 'Нет данных',
    };

    return labels[status];
  }

  public getConfigStateLabel(item: MinioConfigurationItem): string {
    const labels: Record<MinioConfigurationItem['state'], string> = {
      ready: 'Готово',
      attention: 'Проверить',
      missing: 'Не задано',
    };

    return labels[item.state];
  }

  public trackByCheckId(_index: number, check: MinioHealthCheck): string {
    return check.id;
  }

  public trackByConfigurationLabel(_index: number, item: MinioConfigurationItem): string {
    return item.label;
  }

  public trackByStepTitle(_index: number, item: MinioNextStep): string {
    return item.title;
  }

  private findCheck(id: string): MinioHealthCheck | null {
    return this.status?.checks.find((check) => check.id === id) ?? null;
  }

  private get isAdminAllowedIpReady(): boolean {
    const adminIpState = this.status?.configuration.find((item) => item.label === 'Admin allowed IP')?.state;

    return adminIpState === 'ready';
  }
}
